import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Headphones, Building2, Home, Plane, Plug, Bluetooth, VolumeX, Users, Volume2,
  Luggage, Package, Check, RotateCcw, ArrowRight, ArrowLeft, Table2,
} from "lucide-react";
import { EASE } from "@/components/landing/Reveal";
import { trackCta } from "@/lib/track";

const ICONS = {
  desk: Building2, hybrid: Home, go: Plane,
  wired: Plug, wireless: Bluetooth,
  controlled: VolumeX, shared: Users, high: Volume2,
  fold: Luggage, standard: Package, yes: Luggage, no: Package,
};

const TREE = {
  q1: {
    step: 1,
    title: "Where do you take most of your calls?",
    sub: "Select the workplace that best reflects a normal week.",
    why: "Mission is a wired USB family positioned for call-intensive, desk-based work. Focus 6 is Bluetooth wireless and positioned around mobility, focus and hybrid work.",
    options: [
      { id: "desk", title: "Fixed desk / office", desc: "Mostly one computer • structured desk setup", next: "q2_desk" },
      { id: "hybrid", title: "Home + Office", desc: "Hybrid schedule • move between locations", next: "q2_hybrid" },
      { id: "go", title: "On the Go", desc: "Travel • cafés • airports • customer sites", next: "q2_go" },
    ],
  },
  q2_desk: {
    step: 2,
    title: "At your fixed desk, what matters more?",
    sub: "Both paths support professional calls. Choose the operating style you prefer.",
    options: [
      { id: "wired", title: "Plug in and stay ready", desc: "Reliable USB-C / USB-A • inline controls • no battery planning", next: "q3_mission" },
      { id: "wireless", title: "Move around freely", desc: "Bluetooth • headset controls • up to 50 m wireless range", next: "q3_travel" },
    ],
  },
  q2_hybrid: {
    step: 2,
    title: "How do you want to connect across home and office?",
    sub: "Choose the behavior that removes the most friction from your day.",
    options: [
      { id: "wired", title: "One primary PC, wired", desc: "USB simplicity • repeatable setup • call-intensive days", next: "q3_mission" },
      { id: "wireless", title: "PC + mobile, wireless", desc: "Bluetooth mobility • easy movement • hybrid focus", next: "q3_travel" },
    ],
  },
  q2_go: {
    step: 2,
    title: "Do you pack the headset away often?",
    sub: "The Fold model is designed for easier travel between locations.",
    why: "Either route keeps the wireless Focus 6 experience: hybrid ANC, enhanced noise reduction, replaceable battery and magnetic ear cushions.",
    options: [
      { id: "fold", title: "Yes, compact packing matters", desc: "Frequent travel • smaller packed form • hard case", result: "focus620fold" },
      { id: "standard", title: "No, standard design is fine", desc: "Daily mobility • soft pouch • same core Focus 6 experience", result: "focus620" },
    ],
  },
  q3_mission: {
    step: 3,
    title: "How demanding is your audio environment?",
    sub: "Choose the closest match. This step selects the Mission tier.",
    why: "Mission step-up logic — 400 = 1 mic, essential. 600 = 2 mics, Open Office certified. 800 = 4 mics plus feedback ANC.",
    options: [
      { id: "controlled", title: "Controlled", desc: "Quiet desk • routine calls • essential clarity", result: "mission400" },
      { id: "shared", title: "Shared / open office", desc: "Nearby talkers • long call days • busy light useful", result: "mission600" },
      { id: "high", title: "High distraction", desc: "You need ANC plus the strongest Mission microphone tier", result: "mission800" },
    ],
  },
  q3_travel: {
    step: 3,
    title: "Will you pack the headset for frequent travel?",
    sub: "Choose a final configuration.",
    options: [
      { id: "yes", title: "Yes, I travel often", desc: "Recommend the compact Fold configuration", result: "focus620fold" },
      { id: "no", title: "No, mostly home / office", desc: "Recommend the standard configuration", result: "focus620" },
    ],
  },
};

const RESULTS = {
  mission400: {
    name: "Mission 400 Series",
    bestFor: "Best for controlled desks and dependable wired calling.",
    why: "A straightforward USB headset for routine calls where simplicity, clear speech and all-day wear matter most.",
    features: [
      "1 noise-canceling microphone with AI noise reduction",
      "Super wideband audio and 32 mm speaker drivers",
      "USB-C with USB-A compatibility",
      "Choice of foam or leather ear cushions",
    ],
    also: "Mission 600 when nearby chatter becomes frequent.",
  },
  mission600: {
    name: "Mission 600 Series",
    bestFor: "Best for shared and open-office call days.",
    why: "The balanced wired choice when nearby voices, call volume and all-day comfort require a step up.",
    features: [
      "2-microphone design with AI noise reduction",
      "Microsoft Open Office certified",
      "Busy light, boom rotation mute and voice prompts",
      "Padded headband, carrying case and inline controls",
    ],
    also: "Mission 800 if you also need ANC for what you hear.",
  },
  mission800: {
    name: "Mission 800 Series",
    bestFor: "Best for high-distraction, fixed-desk work.",
    why: "The premium wired Mission tier for users who need both strong transmit performance and active noise cancellation.",
    features: [
      "4-microphone design with AI noise reduction",
      "Feedback ANC for wearer focus",
      "Microsoft Open Office certified",
      "Advanced inline controller, busy light and carrying case",
    ],
    also: "Focus 620 if wireless mobility is more important than a wired workflow.",
  },
  focus620: {
    name: "Focus 620",
    bestFor: "Best for hybrid mobility without frequent packing.",
    why: "The consumer Focus 6 model for switching between work locations while keeping wireless freedom, focus and comfort.",
    features: [
      "Bluetooth stereo wireless design",
      "Acoustic Fence 2.0 with enhanced noise reduction",
      "Hybrid ANC, spatial audio and 32 mm drivers",
      "Up to 25 hours talk time with ANC and online indicator on",
      "Replaceable battery and magnetic ear cushions",
    ],
    also: "Focus 620 Fold when compact packing and travel are frequent.",
  },
  focus620fold: {
    name: "Focus 620 Fold",
    bestFor: "Best for public spaces and frequent travel.",
    why: "The consumer Focus 6 configuration that adds a compact fold design and travel-focused cable/case to the same core wireless experience.",
    features: [
      "Foldable design for easier travel",
      "3.5 mm to USB cable for media listening while traveling",
      "Acoustic Fence 2.0, enhanced noise reduction and hybrid ANC",
      "Up to 25 hours talk time with ANC and online indicator on",
      "Hard travel case, replaceable battery and cushions",
    ],
    also: "Focus 620 standard when packing size is not a priority.",
  },
};

const PORTFOLIO = [
  { model: "Mission 400", connection: "Wired USB", workplace: "Controlled desk", step: "1 mic • AI noise reduction" },
  { model: "Mission 600", connection: "Wired USB", workplace: "Shared / open office", step: "2 mics • Open Office certified" },
  { model: "Mission 800", connection: "Wired USB", workplace: "High-distraction desk", step: "4 mics • feedback ANC" },
  { model: "Focus 620", connection: "Bluetooth wireless", workplace: "Home + office", step: "Hybrid ANC • Acoustic Fence 2.0" },
  { model: "Focus 620 Fold", connection: "Bluetooth wireless", workplace: "Travel / public spaces", step: "Focus 620 core + foldable travel design" },
];

const HOW_IT_WORKS = [
  { num: "1", title: "Workplace", desc: "Where calls happen most" },
  { num: "2", title: "Connection", desc: "Wired stability or wireless freedom" },
  { num: "3", title: "Distractions", desc: "Match noise and mobility needs" },
];

const Screen = ({ k, children }) => (
  <motion.div
    key={k}
    initial={{ opacity: 0, y: 28 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={{ duration: 0.55, ease: EASE }}
  >
    {children}
  </motion.div>
);

export default function FitCheckPage() {
  const navigate = useNavigate();
  const [node, setNode] = useState("intro");
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showTable, setShowTable] = useState(false);

  const start = () => { setNode("q1"); setResult(null); setHistory([]); setShowTable(false); };
  const pick = (opt) => {
    if (opt.result) {
      setResult(opt.result);
    } else {
      setHistory((h) => [...h, node]);
      setNode(opt.next);
    }
  };
  const back = () => {
    const prev = history[history.length - 1];
    setHistory((h) => h.slice(0, -1));
    if (prev) setNode(prev);
  };

  const question = node !== "intro" && !result ? TREE[node] : null;
  const rec = result ? RESULTS[result] : null;

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200 bg-white/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <a href="/" className="flex items-center gap-2.5">
            <span className="w-8 h-8 rounded-md bg-[#024AD8] grid place-items-center font-display font-extrabold text-white text-sm">hp</span>
            <span className="font-display font-bold text-slate-900 tracking-tight text-lg">
              HP Poly <span className="text-[#024AD8]">Fit Check</span>
            </span>
          </a>
          <a href="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Gen 2 Migration
          </a>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <AnimatePresence mode="wait">
          {node === "intro" && !result && (
            <Screen k="intro">
              <div className="max-w-3xl">
                <p className="font-mono text-xs uppercase tracking-[0.18em] font-semibold text-[#024AD8]">
                  HP Poly · Interactive
                </p>
                <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.06]">
                  Find Your HP Poly Fit
                </h1>
                <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
                  A click-through headset finder for every workplace. Choose where you work, how you
                  connect and how much noise you face — the fit check routes you to a recommended
                  HP Poly headset.
                </p>
                <p className="mt-4 text-sm text-slate-500">
                  Recommendation = use-case fit, not a universal "best" headset.
                </p>
              </div>

              <div className="mt-12 grid sm:grid-cols-3 gap-4">
                {HOW_IT_WORKS.map((s) => (
                  <div key={s.num} className="rounded-2xl border border-[#2a2a2a] bg-[#1a1a1a] p-6 hover:border-[#024AD8]/60 transition-colors duration-300">
                    <span className="w-10 h-10 rounded-xl bg-slate-900 text-white grid place-items-center font-display font-bold">
                      {s.num}
                    </span>
                    <h3 className="mt-5 font-display font-bold text-slate-900 tracking-tight">{s.title}</h3>
                    <p className="mt-1.5 text-sm text-slate-400">{s.desc}</p>
                  </div>
                ))}
              </div>

              <button
                data-testid="fit-start-button"
                onClick={start}
                className="group mt-12 inline-flex items-center gap-2.5 bg-[#024AD8] hover:bg-[#0238A8] text-white font-semibold px-8 py-4 rounded-md transition-[background-color,transform] duration-300 active:scale-[0.98]"
              >
                Start the Fit Check
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </Screen>
          )}

          {question && (
            <Screen k={node}>
              <div className="flex items-center gap-4 mb-10">
                <span data-testid="fit-progress" className="font-mono text-xs uppercase tracking-[0.18em] font-semibold text-[#024AD8]">
                  Question {question.step} of 3
                </span>
                <div className="flex gap-1.5">
                  {[1, 2, 3].map((s) => (
                    <span key={s} className={`h-1.5 rounded-full transition-all duration-500 ${s <= question.step ? "w-10 bg-[#024AD8]" : "w-5 bg-slate-200"}`} />
                  ))}
                </div>
              </div>

              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 leading-[1.15] max-w-2xl">
                {question.title}
              </h2>
              <p className="mt-4 text-base text-slate-600 max-w-xl">{question.sub}</p>

              <div className={`mt-10 grid gap-4 ${question.options.length === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2 max-w-3xl"}`}>
                {question.options.map((opt) => {
                  const Icon = ICONS[opt.id] || Headphones;
                  return (
                    <button
                      key={opt.id}
                      data-testid={`fit-option-${opt.id}`}
                      onClick={() => pick(opt)}
                      className="group text-left rounded-2xl border border-[#2a2a2a] bg-[#1a1a1a] p-7 transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1.5 hover:border-[#024AD8] hover:shadow-[0_24px_48px_-20px_rgba(2,74,216,0.3)]"
                    >
                      <span className="w-12 h-12 rounded-2xl bg-[#024AD8] text-white grid place-items-center transition-colors duration-300 group-hover:bg-[#FF5050]">
                        <Icon className="w-5 h-5" />
                      </span>
                      <h3 className="mt-6 font-display text-lg font-bold tracking-tight text-white leading-snug">
                        {opt.title}
                      </h3>
                      <p className="mt-2.5 text-sm text-slate-400 leading-relaxed">{opt.desc}</p>
                      <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#024AD8] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        Select <ArrowRight className="w-4 h-4" />
                      </span>
                    </button>
                  );
                })}
              </div>

              {question.why && (
                <div className="mt-10 rounded-xl border border-[#2a2a2a] bg-[#1a1a1a] p-5 max-w-3xl">
                  <p className="font-mono text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-1.5">Why we ask</p>
                  <p className="text-sm text-slate-400 leading-relaxed">{question.why}</p>
                </div>
              )}

              <div className="mt-10 flex items-center gap-5">
                {history.length > 0 && (
                  <button onClick={back} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors">
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                )}
                <button data-testid="fit-restart-button" onClick={start} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors">
                  <RotateCcw className="w-4 h-4" /> Restart
                </button>
              </div>
            </Screen>
          )}

          {rec && (
            <Screen k={result}>
              <div data-testid="fit-result" className="grid lg:grid-cols-12 gap-10 items-start">
                <div className="lg:col-span-7">
                  <p className="font-mono text-xs uppercase tracking-[0.18em] font-semibold text-[#024AD8]">
                    Your best fit
                  </p>
                  <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 leading-[1.08]">
                    HP Poly <span data-testid="fit-result-product-name" className="text-[#024AD8]">{rec.name}</span>
                  </h2>
                  <p className="mt-4 text-lg font-semibold text-slate-900">{rec.bestFor}</p>
                  <p className="mt-3 text-base text-slate-600 leading-relaxed max-w-xl">{rec.why}</p>

                  <ul className="mt-8 space-y-3.5">
                    {rec.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm sm:text-base text-slate-700">
                        <span className="mt-0.5 w-5 h-5 rounded-full bg-[#EAF0FC] grid place-items-center shrink-0">
                          <Check className="w-3 h-3 text-[#024AD8]" />
                        </span>
                        {f}
                      </li>
                    ))}
                  </ul>

                  <p className="mt-8 text-sm text-slate-500 max-w-xl">
                    <span className="font-semibold text-slate-700">Also consider:</span> {rec.also}
                  </p>

                  <div className="mt-10 flex flex-wrap gap-4">
                    <button
                      data-testid="fit-compare-toggle"
                      onClick={() => setShowTable((v) => !v)}
                      className="inline-flex items-center gap-2 text-slate-900 font-semibold px-6 py-3 rounded-md border border-slate-800 hover:bg-slate-900 hover:text-white transition-[background-color,color] duration-300"
                    >
                      <Table2 className="w-4 h-4" /> Compare all models
                    </button>
                    <button
                      data-testid="fit-specialist-cta"
                      onClick={() => { trackCta("fit-check"); navigate("/", { state: { scrollTo: "contact" } }); }}
                      className="group inline-flex items-center gap-2 bg-[#024AD8] hover:bg-[#0238A8] text-white font-semibold px-6 py-3 rounded-md transition-[background-color] duration-300"
                    >
                      Connect with your HP Poly specialist
                      <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                    <button
                      data-testid="fit-start-again-button"
                      onClick={start}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 transition-colors px-2"
                    >
                      <RotateCcw className="w-4 h-4" /> Start again
                    </button>
                  </div>
                </div>

                <div className="lg:col-span-5">
                  <div className="rounded-3xl bg-[#1a1a1a] p-10 lg:p-12 relative overflow-hidden">
                    <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#024AD8]/15 blur-3xl pointer-events-none" />
                    <span className="relative mx-auto w-24 h-24 rounded-3xl bg-white/10 border border-white/15 grid place-items-center">
                      <Headphones className="w-11 h-11 text-[#6D8FF0]" />
                    </span>
                    <p className="relative mt-8 text-center font-display text-2xl font-bold text-white tracking-tight">{rec.name}</p>
                    <p className="relative mt-2 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
                      HP Poly recommended fit
                    </p>
                  </div>
                </div>
              </div>

              <AnimatePresence>
                {showTable && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.5, ease: EASE }}
                    className="overflow-hidden"
                  >
                    <div className="mt-12 rounded-2xl border border-[#2a2a2a] overflow-hidden bg-[#1a1a1a]">
                      <div className="px-7 py-5 border-b border-[#2a2a2a] bg-[#1a1a1a]/70">
                        <h3 className="font-display font-bold text-slate-900 tracking-tight">Portfolio view — the recommendation logic at a glance</h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left min-w-[720px]">
                          <thead>
                            <tr className="border-b border-[#262626]">
                              {["Model", "Connection", "Best-fit workplace", "Noise / focus step-up"].map((h) => (
                                <th key={h} className="px-7 py-4 font-mono text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {PORTFOLIO.map((row) => (
                              <tr key={row.model} className={`border-b border-[#262626] last:border-0 ${rec.name.startsWith(row.model.split(" Fold")[0]) && (row.model.includes("Fold") === rec.name.includes("Fold")) ? "bg-[#024AD8]/15" : ""}`}>
                                <td className="px-7 py-4 font-display font-bold text-white whitespace-nowrap">{row.model}</td>
                                <td className="px-7 py-4 text-sm text-slate-400">{row.connection}</td>
                                <td className="px-7 py-4 text-sm text-slate-400">{row.workplace}</td>
                                <td className="px-7 py-4 text-sm text-slate-400">{row.step}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </Screen>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
